import React from "react";
import { useLocation, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { usePmoNavigation } from "hooks/usePmoNavigation";
import { SavedWorkItem } from "hooks/useTaskForm";
import { useResolveWorkItemKey, useResolveProjectKey } from "service/project/hooks/useResolvePmoKeys";
import { getEpic } from "service/project/api/epic";
import { epicQueryKey } from "service/project/hooks/useEpicQueries";
import TaskDetailContent from "components/modules/ProjectManagement/Projects/TaskDetailContent";
import { showEpicCreatedToast } from "components/modules/ProjectManagement/Epic/EpicCreatedToast";
import Spinner from "components/Loaders/Spinner";
import EmptyState from "components/common/EmptyState";
import { WorkItemLinkState } from "types/project/tasks";
import { showToast } from "utils/toast";

const TaskDetailShell = () => {
  const { projectKey, workItemKey } = useParams<{
    projectKey: string;
    workItemKey?: string;
  }>();

  const queryClient = useQueryClient();
  const { toBacklog, toEpic, toEpics, toProjects, orgSlug } = usePmoNavigation();
  const location = useLocation();
  const readOnlyNote = (location.state as WorkItemLinkState | null)?.readOnlyNote;
  // Set by an Epic's Add child, so creating the child goes back to the Epic.
  const returnToEpic = new URLSearchParams(location.search).get("returnToEpic");

  // Resolve keys to UUIDs
  const { data: resolvedWorkItem, isLoading: isResolvingWorkItem } = useResolveWorkItemKey(
    orgSlug,
    projectKey,
    workItemKey,
  );

  const { data: resolvedProject, isLoading: isResolvingProject } = useResolveProjectKey(
    orgSlug,
    projectKey
  );

  const isResolving = (workItemKey && workItemKey !== "create-task") ? isResolvingWorkItem : isResolvingProject;

  if (isResolving) {
    return (
      <div className="flex-1 overflow-hidden flex flex-col bg-primary h-[calc(100vh-64px)]">
        <Spinner />
      </div>
    );
  }

  // Handle Project Not Found error state
  if (resolvedProject?.status === false) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-primary min-h-[60vh]">
        <EmptyState
          title="Project Not Found"
          description={resolvedProject?.message || "This project might have been deleted or you may not have access to it."}
          buttonLabel="Back to Projects"
          onButtonClick={() => toProjects()}
          showAddIcon={false}
        />
      </div>
    );
  }

  // Handle Work Item Not Found error state
  if (workItemKey && workItemKey !== "create-task" && resolvedWorkItem?.status === false) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-primary min-h-[60vh]">
        <EmptyState
          title="Task Not Found"
          description={resolvedWorkItem?.message || "This task might have been deleted or you may not have access to it."}
          buttonLabel="Back to Backlog"
          onButtonClick={() => {
            if (projectKey) {
              toBacklog(projectKey);
            } else {
              toProjects();
            }
          }}
          showAddIcon={false}
        />
      </div>
    );
  }

  const projectId = (workItemKey && workItemKey !== "create-task") ? resolvedWorkItem?.projectUuid : resolvedProject?.projectUuid;
  const taskId = resolvedWorkItem?.workItemUuid;
  const isCreateMode = workItemKey === "create-task" || !workItemKey;

  const close = () =>
    returnToEpic ? toEpic(projectKey as string, returnToEpic) : toBacklog(projectKey as string);

  // Epics never show in the Backlog, so a new one goes to the Epics tab, with a link
  // to its page. The create response only has its uuid, so its key is read from the Epic.
  const confirmEpicCreated = async (epicUuid?: string) => {
    toEpics(projectKey as string);
    if (!projectId || !epicUuid) {
      showToast.success("Epic created");
      return;
    }
    try {
      // Same key as the Epic page's default view, so opening it is instant.
      const { epic } = await queryClient.fetchQuery({
        queryKey: [...epicQueryKey(epicUuid), { hideDone: true }],
        queryFn: () => getEpic(projectId, epicUuid, true),
      });
      showEpicCreatedToast(epic, () => toEpic(projectKey as string, epic.workItemKey));
    } catch {
      showToast.success("Epic created");
    }
  };

  return (
    <TaskDetailContent
      projectId={projectId || ""}
      taskId={taskId || null}
      projectKey={projectKey}
      isCreateMode={isCreateMode}
      onClose={close}
      onSuccessSave={(saved: SavedWorkItem) => (saved.isNewEpic ? confirmEpicCreated(saved.workItemUuid) : close())}
      layout="page"
      readOnly={!!readOnlyNote && !isCreateMode}
      readOnlyNote={readOnlyNote}
    />
  );
};

export default TaskDetailShell;
